import 'bootstrap/dist/css/bootstrap.min.css';
import React, { useContext, useEffect, useState } from 'react';
import { Button } from 'react-bootstrap';
import { useNavigate } from 'react-router-dom';
import ApiFetch from '../ApiFetch';
import { useTranslation } from "react-i18next";
import { Card } from '@mui/material';
import { LoginInfo } from '../../App';

// CharacterProfileCard コンポーネント単体
const CharacterProfileCard = (props) => {
  // 翻訳
  const { t } = useTranslation();
  // ログイン情報
  const { loginInfo } = useContext(LoginInfo);
  // ナビゲート
  const navigate = useNavigate();
  // カードサイズ（デフォルト値とPCサイズ）
  const [cardWidth, setCardWidth] = useState(180);
  // 3:4のアスペクト比に変更
  const [cardHeight, setCardHeight] = useState(240);
  const pcWidth = 260;
  const pcHeight = 347; // 3:4のアスペクト比でPCサイズを設定 (260 * 4/3)

  // ウィンドウサイズに基づいてカードサイズを調整
  useEffect(() => {
    const handleResize = () => {
      // PC判定（768px以上を一般的なPCサイズとする）
      if (window.innerWidth >= 992) {
        setCardWidth(pcWidth);
        setCardHeight(pcHeight);
      } else {
        // スマホサイズは画面幅に合わせて2列表示になるように調整
        // 画面の半分から余白を引いた幅に設定
        const viewportWidth = window.innerWidth;
        const cardMargin = 10; // カード間の余白
        const edgeMargin = 10; // 画面端の余白
        // 2列表示のため、利用可能な幅の半分からマージンを引く
        const calculatedWidth = Math.floor((viewportWidth - (edgeMargin * 2) - cardMargin) / 2);
        setCardWidth(calculatedWidth);
        // 3:4のアスペクト比を維持
        setCardHeight(Math.floor(calculatedWidth * (4/3)));
      }
    };

    // 初回レンダリング時に実行
    handleResize();

    // リサイズイベントリスナーを追加
    window.addEventListener('resize', handleResize);

    // クリーンアップ
    return () => {
      window.removeEventListener('resize', handleResize);
    };
  }, []);

  // チャットページへの遷移処理
  const handleChatClick = (e) => {
    e.stopPropagation(); // カード全体のクリックイベントを停止
    navigate("/aitalk/"+props.data.group_id);
  };

  // フォントサイズ計算（カードサイズに応じて調整）
  const titleFontSize = cardWidth === pcWidth ? '16px' : '12px';
  const descFontSize = cardWidth === pcWidth ? '14px' : '10px';
  const buttonFontSize = cardWidth === pcWidth ? '14px' : '12px';
  
  // アイコンサイズ計算
  const iconSize = cardWidth === pcWidth ? 35 : 25;

  return (
    <div style={{ 
      width: cardWidth, 
      flexShrink: 0,
      margin: window.innerWidth >= 992 ? "0 10px 0 0" : "0 5px 10px 5px"
    }}>
      {/* イメージカード（サムネイル部分） */}
      <Card 
        sx={{ width: cardWidth, height: cardHeight }} 
        style={{ 
          cursor: "pointer", 
          position: "relative",
          backgroundImage: `url(${ApiFetch.API_PROTOCOL+"//"+ApiFetch.IMG_HOST+"/character/post/t/"+props.data.id+".webp"})`,
          backgroundSize: "cover",
          backgroundPosition: "center",
          marginBottom: "5px"
        }} 
        onClick={() => {navigate("/character/"+props.data.group_id)}}
      >
        {/* キャラクターアイコン（右下に配置） */}
        <div style={{
          position: "absolute",
          bottom: "5px",
          right: "5px",
          width: `${iconSize}px`,
          height: `${iconSize}px`,
          borderRadius: "5px",
          overflow: "hidden",
          border: "2px solid white",
          boxShadow: "0 1px 3px rgba(0,0,0,0.3)"
        }}>
          <img 
            src={ApiFetch.API_PROTOCOL+"//"+ApiFetch.IMG_HOST+"/character/post/t/"+props.data.group_id+"_in.webp"}
            style={{width: "100%", height: "100%", objectFit: "cover"}}
            alt=""
          />
        </div>
      </Card>

      {/* キャラクター名とテキスト情報部分（サムネイルの外） */}
      <div style={{ marginBottom: "5px" }}>
        {/* キャラクター名 */}
        <div style={{
          fontSize: titleFontSize, 
          fontWeight: "bold",
          overflow: "hidden", 
          textOverflow: "ellipsis", 
          whiteSpace: "nowrap",
          marginBottom: "2px"
        }}>
          {props.data.character_name.substr(0, 15)}
        </div>
        
        {/* 説明文 - 2行固定 */}
        <div style={{
          fontSize: descFontSize, 
          overflow: "hidden",
          display: "-webkit-box", 
          WebkitLineClamp: 2, 
          WebkitBoxOrient: "vertical",
          height: cardWidth === pcWidth ? '2.8em' : '2.4em',
          lineHeight: cardWidth === pcWidth ? '1.4' : '1.2',
          color: "#555"
        }}>
          {props.data.description}
        </div>
      </div>
      
      {/* 「このキャラとチャット」ボタン */}
      <Button
        variant="primary"
        size={cardWidth === pcWidth ? "md" : "sm"}
        style={{ 
          width: "100%", 
          fontSize: buttonFontSize,
          padding: cardWidth === pcWidth ? "6px 0" : "4px 0"
        }}
        onClick={handleChatClick}
      >
        このキャラとチャット
      </Button>
    </div>
  );
}

// CharacterSlider - 横スライドコンテナコンポーネント
const CharacterSlider = ({ characters }) => {
  // スマホ表示用の状態
  const [isMobile, setIsMobile] = useState(false);

  // 画面サイズ検出
  useEffect(() => {
    const handleResize = () => {
      setIsMobile(window.innerWidth < 992);
    };
    
    handleResize();
    window.addEventListener('resize', handleResize);
    
    return () => {
      window.removeEventListener('resize', handleResize);
    };
  }, []);

  return (
    <div className="character-slider-container" style={{ position: "relative", margin: "0 0 15px -15px" }}>
      {isMobile ? (
        // モバイル用2列グリッドレイアウト
        <div className="character-grid" style={{
          display: "flex",
          flexWrap: "wrap",
          justifyContent: "center",
          padding: "10px 0"
        }}>
          {characters.map((character) => (
            <CharacterProfileCard 
              key={character.group_id} 
              data={character} 
            />
          ))}
        </div>
      ) : (
        // PC用横スクロールレイアウト
        <div className="character-slider" style={{
          display: "flex",
          overflowX: "auto",
          scrollbarWidth: "none", // Firefox用
          padding: "10px 0",
          WebkitOverflowScrolling: "touch" // iOSのスムーススクロール
        }}>
          {/* 最初の要素に左マージンを追加 */}
          <div style={{ flexShrink: 0, width: "10px" }}></div>
          
          {/* キャラクターカードをマップ */}
          {characters.map((character) => (
            <CharacterProfileCard 
              key={character.group_id} 
              data={character} 
            />
          ))}
          
          {/* 最後の要素に右マージンを追加 */}
          <div style={{ flexShrink: 0, width: "10px" }}></div>
        </div>
      )}
    </div>
  );
};

// スタイルを追加 - コンポーネント外で定義
const addGlobalStyle = () => {
  // スクロールバーを非表示にするCSS
  const style = document.createElement('style');
  style.textContent = `
    .character-slider::-webkit-scrollbar {
      display: none;
    }
    
    @media (min-width: 992px) {
      .character-slider {
        scroll-behavior: smooth;
      }
    }
  `;
  document.head.appendChild(style);
};

// コンポーネントがマウントされたらスタイルを追加
if (typeof window !== 'undefined') {
  addGlobalStyle();
}

export { CharacterProfileCard, CharacterSlider };
export default CharacterSlider;